import styled from "styled-components";
import { useEffect, useState, useMemo } from "react";
import { useRecoilState } from "recoil";
import { Link, useParams } from "react-router-dom";
import { MDBCard, MDBCardBody, MDBInput, MDBTable, MDBTableBody } from "mdb-react-ui-kit";
import { useDebouncedCallback } from "use-debounce";
import { Row, Col } from "../App.styles";
import { queryState, styleState, metricState, useLeaderboardDataQuery } from "./Leaderboard.state";
import MyTeam from "./MyTeam";
import { Button } from "../App.styles";
import axios from "axios";
import { Alert, Loadable, Refresher } from "../common";
import { useAuthUser } from "react-auth-kit";
import { useUserQuery } from "./User.state";
import PageTabs from "./PageTabs";
import ImageThumbnail from "../common/ImageThumbnail";
import { useTranslation } from 'react-i18next'
import { useDecision } from '@optimizely/react-sdk'
import { usePageListeners } from "../common/Refresher.state";
import ROUTE_PATTERNS from "../consts/routes";

const PhotoCell = styled.div`
  width: 75px;
  height: 75px;
  background-color: ${({ theme }) => theme.placeholderColor};
`;

const LeaderboardTableBody = styled(MDBTableBody)`
  th,
  td {
    padding: 0.3rem;
  }
`;

const HeaderRow = styled(Row)`
  margin-bottom: 1rem;

  ${Row} {
    width: 100%;

    ${Col}:first-child {
      justify-content: center;
      align-items: flex-end;
    }

    ${Col}:last-child {
      align-items: flex-start;
    }
  }
`;

const RecalculateButton = styled(Button)`
  box-sizing: content-box;
  min-width: 1rem;
  padding-left: 1rem;
  padding-right: 1rem;
`;

const LeaderboardRow = ({
  game_id,
  id,
  name,
  photo,
  points,
  place,
  completed,
}) => (
  <tr>
    <th>{place}</th>
    <td>
      <PhotoCell>
        {photo && (
          <ImageThumbnail
            alt={name}
            src={photo.url}
          />
        )}
      </PhotoCell>
    </td>
    <td>
      <Link to={`/teams/stream/${game_id}/${id}`}>{name}</Link>
    </td>
    <td>{points}</td>
    <td>{completed}</td>
  </tr>
);

const Leaderboard = () => {
  const { t } = useTranslation();
  const [alert, setAlert] = useState("");
  const { game_id } = useParams();
  const auth = useAuthUser()();
  const { token } = auth || {};
  const { data, isFetching } = useLeaderboardDataQuery(game_id);
  const [style, setStyle] = useRecoilState(styleState);
  const [metric, setMetric] = useRecoilState(metricState);
  const [query, setQuery] = useRecoilState(queryState);
  const [value, setValue] = useState("");
  const callback = useDebouncedCallback(setQuery, 500);
  const handler = ({ target }) => {
    const { value } = target;
    setValue(value);
    callback(value);
  };
  const leaderboard = useMemo(() => data?.teams || [], [data?.teams])
  const myTeam = useMemo(() => data?.my, [data?.my])
  const userQuery = useUserQuery();
  const [decisionMetric] = useDecision('leaderboard-metric');
  let [recalculateVisible, setRecalculateVisible] = useState(false);
  let [recalculateInitiated, setrecalculateInitiated] = useState(false);

  useEffect(() => {
    setValue(query);

    if (
      userQuery?.game?.administrator ||
      userQuery?.game?.coordinator ||
      userQuery?.game?.super_admin
    ) {
      setRecalculateVisible(true);
    }

    if (recalculateInitiated) {
      setAlert(
        "The leaderboard has been queued for recalculation. It may take a number of minutes to complete. The leaderboard will be refreshed automatically when completed."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const recalculate = () => {
    if (!token) {
      return;
    }

    axios
      .patch(
        process.env.REACT_APP_API_RECALCULATE_LEADERBOARD,
        {
          game_id: game_id,
        },
        {
          headers: {
            "X-Whitelabel": "ss",
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        }
      )
      .then((res) => {
        setrecalculateInitiated(true);
        setAlert(
          "The leaderboard has been queued for recalculation. It may take a number of minutes to complete. The leaderboard will be refreshed automatically when completed."
        );
        window.scrollTo({ top: 0, behavior: "smooth" });
      })
      .catch((err) => console.error(err));
  };

  const filterTabs = [{
      active: style === '"alltime|daily|weekly"',
      onClick: e => {
        e.preventDefault()
        setStyle('"alltime|daily|weekly"')
      },
      label: t('All Time')
  }, {
      active: style === 'daily',
      onClick: e => {
        e.preventDefault()
        setStyle('daily')
      },
      label: t('Daily')
  }, {
      active: style === 'weekly',
      onClick: e => {
        e.preventDefault()
        setStyle('weekly')
      },
      label: t('Weekly')
  }]

  const metricTabs = [{
      active: metric === 'points',
      onClick: e => {
        e.preventDefault()
        setMetric('points')
      },
      label: t('Points')
  }, {
      active: metric === 'completed',
      onClick: e => {
        e.preventDefault()
        setMetric('completed')
      },
      label: t('Completed')
  }]

  const gameIds = useMemo(() => [data?.game?.id], [data])
  const listeners = usePageListeners(ROUTE_PATTERNS.TEAMS_LEADERBOARD, { gameIds })
  return (
    <>
      <Alert color="info">{alert}</Alert>
      <HeaderRow>
        <Col size="2" className="justify-start">
          {recalculateVisible ? (
            <RecalculateButton onClick={recalculate}>
              Recalculate
            </RecalculateButton>
          ) : (
            ""
          )}
        </Col>
        <Col size="4">
          <PageTabs tabs={filterTabs}/>
        </Col>
        <Col size="2">
            {
                decisionMetric?.enabled
                    ? <PageTabs tabs={metricTabs}/>
                    : null
            }
        </Col>
        <Col size="4">
          <MDBInput
            label="Filter leaderboard"
            value={value}
            onChange={handler}
          />
        </Col>
      </HeaderRow>
      <Loadable loading={isFetching}>
        {
            myTeam
                ? (
                    <HeaderRow>
                        <Col lg="4">
                            <MDBCard className="flex-grow-1">
                                <MDBCardBody>
                                    <MyTeam myTeam={myTeam} />
                                </MDBCardBody>
                            </MDBCard>
                        </Col>
                    </HeaderRow>
                )
                : null
        }
        <Row>
            <div>
            <MDBTable striped>
                <LeaderboardTableBody>
                <tr>
                    <th>{t("Rank")}</th>
                    <th>{t("Photo")}</th>
                    <th>{t("Name")}</th>
                    <th>{t("Points")}</th>
                    <th>{t("Completed")}</th>
                </tr>
                {leaderboard.map((table, index) => (
                    <LeaderboardRow
                    key={index}
                    {...table}
                    game_id={game_id}
                    index={index}
                    // Thumbnails are served with ImageThumbnail component that will 
                    // auto populate thumbnail URLs
                    photo={table.assets.photos.find(
                        (photo) => photo.name === "standard"
                    )}
                    />
                ))}
                </LeaderboardTableBody>
            </MDBTable>
            </div>
        </Row>
      </Loadable>
      <Refresher listeners={listeners} />
    </>
  );
};

export default Leaderboard;
